import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Building2, Plus, Trash2, Users, Edit, Search, UserPlus, Key, CreditCard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { SUBSCRIPTION_TIERS, TierKey } from '@/lib/subscriptions';

interface Client {
  id: string;
  name: string;
  description: string | null;
  contact_email: string | null;
  is_active: boolean;
  created_at: string;
  user_count?: number;
  search_count?: number;
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
  });
  
  // New user creation fields
  const [createUser, setCreateUser] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState<'user' | 'super_user'>('super_user');
  const [selectedPlan, setSelectedPlan] = useState<TierKey | 'free'>('free');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Plan assignment dialog state
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogClient, setPlanDialogClient] = useState<Client | null>(null);
  const [assigningPlan, setAssigningPlan] = useState<TierKey | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get counts for each client
      const clientsWithCounts = await Promise.all(
        (data || []).map(async (client) => {
          const [usersRes, searchesRes] = await Promise.all([
            supabase.from('client_users').select('*', { count: 'exact', head: true }).eq('client_id', client.id),
            supabase.from('ted_searches').select('*', { count: 'exact', head: true }).eq('client_id', client.id),
          ]);
          return {
            ...client,
            user_count: usersRes.count || 0,
            search_count: searchesRes.count || 0,
          };
        })
      );

      setClients(clientsWithCounts);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', contact_email: '' });
    setCreateUser(false);
    setUserEmail('');
    setUserFullName('');
    setUserPassword('');
    setUserRole('super_user');
    setSelectedPlan('free');
  };

  const openCreateDialog = () => {
    setEditingClient(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      description: client.description || '',
      contact_email: client.contact_email || '',
    });
    setCreateUser(false);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast({
        variant: "destructive",
        title: "Nome richiesto",
        description: "Inserisci un nome per il cliente",
      });
      return;
    }

    // Validate user creation fields if enabled
    if (createUser && !editingClient) {
      if (!userEmail) {
        toast({
          variant: "destructive",
          title: "Email richiesta",
          description: "Inserisci l'email dell'utente",
        });
        return;
      }
      if (!userPassword || userPassword.length < 6) {
        toast({
          variant: "destructive",
          title: "Password non valida",
          description: "La password deve essere di almeno 6 caratteri",
        });
        return;
      }
    }

    setActionLoading(true);

    try {
      if (editingClient) {
        // Update existing client
        const { error } = await supabase
          .from('clients')
          .update({
            name: formData.name,
            description: formData.description || null,
            contact_email: formData.contact_email || null,
          })
          .eq('id', editingClient.id);

        if (error) throw error;
        toast({ title: "Cliente aggiornato" });
      } else {
        // Create new client
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            name: formData.name,
            description: formData.description || null,
            contact_email: formData.contact_email || null,
          })
          .select()
          .single();

        if (clientError) throw clientError;

        // Create user if requested
        if (createUser && newClient) {
          const { data, error: inviteError } = await supabase.functions.invoke('invite-user', {
            body: {
              email: userEmail,
              fullName: userFullName || formData.name,
              role: userRole,
              clientId: newClient.id,
              password: userPassword,
              productId: selectedPlan !== 'free' ? SUBSCRIPTION_TIERS[selectedPlan].productId : null,
            },
          });

          if (inviteError) {
            console.error('Invite error:', inviteError);
            toast({
              variant: "destructive",
              title: "Errore creazione utente",
              description: inviteError.message,
            });
          } else if (data?.error) {
            toast({
              variant: "destructive",
              title: "Errore creazione utente",
              description: data.error,
            });
          } else {
            toast({ 
              title: "Cliente e utente creati",
              description: `Utente ${userEmail} creato con successo`,
            });
          }
        } else {
          toast({ title: "Cliente creato" });
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleClientActive = async (client: Client) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({ is_active: !client.is_active })
        .eq('id', client.id);

      if (error) throw error;

      setClients(clients.map(c => 
        c.id === client.id ? { ...c, is_active: !c.is_active } : c
      ));
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    }
  };

  const deleteClient = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo cliente? Tutti i dati associati verranno eliminati.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setClients(clients.filter(c => c.id !== id));
      toast({ title: "Cliente eliminato" });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    }
  };

  const getPlanLabel = (tierKey: TierKey | 'free') => {
    if (tierKey === 'free') return 'Piano Free';
    return SUBSCRIPTION_TIERS[tierKey].name;
  };

  const openPlanDialog = (client: Client) => {
    setPlanDialogClient(client);
    setAssigningPlan(null);
    setPlanDialogOpen(true);
  };

  const assignPlanToClient = async () => {
    if (!planDialogClient || !assigningPlan) return;
    
    setPlanLoading(true);
    
    try {
      // Get the user associated with this client
      const { data: clientUsers, error: userError } = await supabase
        .from('client_users')
        .select('user_id')
        .eq('client_id', planDialogClient.id)
        .limit(1);
      
      if (userError || !clientUsers || clientUsers.length === 0) {
        console.error('Client users query error:', userError);
        toast({
          variant: "destructive",
          title: "Utente non trovato",
          description: "Nessun utente associato a questo cliente",
        });
        setPlanLoading(false);
        return;
      }
      
      const userId = clientUsers[0].user_id;
      
      // Get the user's email from profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();
      
      if (profileError || !profile?.email) {
        console.error('Profile query error:', profileError);
        toast({
          variant: "destructive",
          title: "Email mancante",
          description: "L'utente non ha un'email valida",
        });
        setPlanLoading(false);
        return;
      }

      const tier = SUBSCRIPTION_TIERS[assigningPlan];
      
      console.log('Assigning plan to:', profile.email, 'priceId:', tier.priceId);
      
      // Call edge function to create subscription with user's actual email
      const { data, error } = await supabase.functions.invoke('admin-create-subscription', {
        body: {
          email: profile.email,
          priceId: tier.priceId,
          clientName: planDialogClient.name,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Piano assegnato",
        description: `${tier.name} assegnato a ${planDialogClient.name} (${profile.email})`,
      });
      
      setPlanDialogOpen(false);
      setPlanDialogClient(null);
      setAssigningPlan(null);
    } catch (error: any) {
      console.error('Error assigning plan:', error);
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    } finally {
      setPlanLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Clienti</h1>
            <p className="text-muted-foreground mt-1">
              Gestisci le aziende clienti della piattaforma
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}
                </DialogTitle>
                <DialogDescription>
                  {editingClient ? 'Modifica i dati del cliente' : 'Crea una nuova azienda cliente con utente e piano'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Client info section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    Dati Cliente
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome Azienda *</Label>
                      <Input
                        id="name"
                        placeholder="es: Acme S.r.l."
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email di Contatto</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="info@azienda.it"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descrizione</Label>
                      <Textarea
                        id="description"
                        placeholder="Note sull'azienda..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* User creation section - only for new clients */}
                {!editingClient && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                          <UserPlus className="w-4 h-4" />
                          Crea Utente Amministratore
                        </div>
                        <Switch
                          checked={createUser}
                          onCheckedChange={setCreateUser}
                        />
                      </div>
                      
                      {createUser && (
                        <div className="space-y-3 animate-fade-in">
                          <div className="space-y-2">
                            <Label htmlFor="userEmail">Email Utente *</Label>
                            <Input
                              id="userEmail"
                              type="email"
                              placeholder="utente@azienda.it"
                              value={userEmail}
                              onChange={(e) => setUserEmail(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="userFullName">Nome Completo</Label>
                            <Input
                              id="userFullName"
                              placeholder="Mario Rossi"
                              value={userFullName}
                              onChange={(e) => setUserFullName(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="userPassword">Password *</Label>
                            <div className="flex gap-2">
                              <Input
                                id="userPassword"
                                type="text"
                                placeholder="Minimo 6 caratteri"
                                value={userPassword}
                                onChange={(e) => setUserPassword(e.target.value)}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setUserPassword(crypto.randomUUID().slice(0, 12))}
                                title="Genera password"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Ruolo Utente</Label>
                            <Select value={userRole} onValueChange={(v: 'user' | 'super_user') => setUserRole(v)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="super_user">Super User (gestisce il team)</SelectItem>
                                <SelectItem value="user">Utente Standard</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>

                    <Separator />
                    
                    {/* Subscription plan section */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <CreditCard className="w-4 h-4" />
                        Piano Abbonamento
                      </div>
                      <div className="space-y-2">
                        <Label>Seleziona Piano</Label>
                        <Select value={selectedPlan} onValueChange={(v: TierKey | 'free') => setSelectedPlan(v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona un piano" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">
                              <div className="flex items-center gap-2">
                                <span>Piano Free</span>
                                <Badge variant="secondary" className="text-xs">Gratuito</Badge>
                              </div>
                            </SelectItem>
                            {(Object.keys(SUBSCRIPTION_TIERS) as TierKey[]).map((tierKey) => {
                              const tier = SUBSCRIPTION_TIERS[tierKey];
                              return (
                                <SelectItem key={tierKey} value={tierKey}>
                                  <div className="flex items-center gap-2">
                                    <span>{tier.name}</span>
                                    <Badge variant="outline" className="text-xs">€{tier.price}/mese</Badge>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {selectedPlan !== 'free' && (
                          <p className="text-xs text-muted-foreground">
                            L'abbonamento verrà attivato manualmente per questo cliente.
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button onClick={handleSubmit} disabled={actionLoading}>
                  {actionLoading ? 'Creazione...' : editingClient ? 'Salva' : createUser ? 'Crea Cliente e Utente' : 'Crea'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Nessun cliente
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Crea il primo cliente per iniziare
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Cliente
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client) => (
              <Card key={client.id} className={`border-0 shadow-sm hover:shadow-md transition-shadow ${!client.is_active ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <CardTitle className="text-lg truncate">{client.name}</CardTitle>
                          {client.contact_email && (
                            <CardDescription className="truncate">
                              {client.contact_email}
                            </CardDescription>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={client.is_active}
                      onCheckedChange={() => toggleClientActive(client)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {client.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {client.description}
                      </p>
                    )}
                    
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{client.user_count} utenti</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{client.search_count} ricerche</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <Badge variant={client.is_active ? "default" : "secondary"}>
                        {client.is_active ? 'Attivo' : 'Disattivato'}
                      </Badge>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPlanDialog(client)}
                          title="Assegna piano"
                        >
                          <CreditCard className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(client)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteClient(client.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Plan Assignment Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assegna Piano</DialogTitle>
            <DialogDescription>
              Assegna un piano di abbonamento a {planDialogClient?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seleziona Piano</Label>
              <Select value={assigningPlan || ''} onValueChange={(v: TierKey) => setAssigningPlan(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un piano..." />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SUBSCRIPTION_TIERS) as TierKey[]).map((tierKey) => {
                    const tier = SUBSCRIPTION_TIERS[tierKey];
                    return (
                      <SelectItem key={tierKey} value={tierKey}>
                        <div className="flex items-center gap-2">
                          <span>{tier.name}</span>
                          <Badge variant="outline" className="text-xs">€{tier.price}/mese</Badge>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {assigningPlan && (
              <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                <p className="text-sm font-medium">{SUBSCRIPTION_TIERS[assigningPlan].name}</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• {SUBSCRIPTION_TIERS[assigningPlan].users} utenti</li>
                  <li>• {SUBSCRIPTION_TIERS[assigningPlan].maxMonitorings === null ? 'Monitoraggi illimitati' : `${SUBSCRIPTION_TIERS[assigningPlan].maxMonitorings} monitoraggi`}</li>
                  <li>• Copertura {SUBSCRIPTION_TIERS[assigningPlan].scope === 'european' ? 'Europea' : 'Nazionale'}</li>
                </ul>
              </div>
            )}
            {!planDialogClient?.contact_email && (
              <p className="text-sm text-destructive">
                ⚠️ Il cliente non ha un'email di contatto configurata
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={assignPlanToClient} 
              disabled={planLoading || !assigningPlan || !planDialogClient?.contact_email}
            >
              {planLoading ? 'Assegnazione...' : 'Assegna Piano'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
